import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { teamChatSignalRController } from 'app/services/signalR';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  clearActiveChannelIfMatchesAction,
  fetchMessagesAction,
  fetchPinnedMessagesAction,
  fetchPresenceAction,
  markAsReadAction,
  pinMessageAction,
  sendMessageAction,
  setActiveChannelAction,
  unpinMessageAction,
  uploadAttachmentAction,
} from 'store/teamChat/actions';
import {
  selectIsTeamChatConnected,
  selectTeamChatChannel,
  selectTeamChatCursor,
  selectTeamChatHasMoreMessages,
  selectTeamChatIsLoadingMessages,
  selectTeamChatIsSendingMessage,
  selectTeamChatMessages,
  selectTeamChatOnlineUsers,
  selectTeamChatPinnedMessageIds,
  selectTeamChatReadReceipts,
} from 'store/teamChat/selectors';

import { messageReferencesEntity } from '../hooks/useEntityReferenceIndex/utils';
import { getEntityReferenceKey } from '../TeamChat.entityReferences.utils';
import {
  getChannelDisplayName,
  getChannelMembers,
  getMemberId,
  getMessageId,
  getMessageSender,
  getMessageTimestamp,
  resolveCurrentChatUserId,
} from '../TeamChat.utils';
import TeamChatHeader from '../TeamChatHeader';
import TeamChatMessageInput from '../TeamChatMessageInput';
import TeamChatMessageList from '../TeamChatMessageList';
import { TeamChatPinnedStrip } from '../TeamChatPins';
import TeamChatReadBy from '../TeamChatReadBy';
import TeamChatReferencesRail from '../TeamChatReferencesRail';
import TeamChatReplyPreview from '../TeamChatReplyPreview';
import TeamChatTypingIndicator from '../TeamChatTypingIndicator';

import useChatEntityFilter from './hooks/useChatEntityFilter';
import { rootStyles } from './TeamChatChannel.styles';

const TeamChatChannel = memo(
  ({
    chatId,
    currentUserId = null,
    fallbackTitle = null,
    headerEndSlot = null,
    isHeaderHidden = false,
    isActive = true,
    canShareCurrentView = false,
    priorityMemberIds = null,
    showEntityReferencesRail = false,
  }) => {
    const dispatch = useDispatch();

    const channel = useSelector(selectTeamChatChannel(chatId));
    const messages = useSelector(selectTeamChatMessages(chatId));
    const cursor = useSelector(selectTeamChatCursor(chatId));
    const hasMore = useSelector(selectTeamChatHasMoreMessages(chatId));
    const isLoadingMessages = useSelector(selectTeamChatIsLoadingMessages(chatId));
    const isSending = useSelector(selectTeamChatIsSendingMessage(chatId));
    const isConnected = useSelector(selectIsTeamChatConnected);
    const onlineUsers = useSelector(selectTeamChatOnlineUsers(chatId));
    const readReceipts = useSelector(selectTeamChatReadReceipts(chatId));

    const pinnedIds = useSelector(selectTeamChatPinnedMessageIds(chatId));
    const members = getChannelMembers(channel);

    const effectiveOnlineIds = useMemo(() => {
      const ids = new Set(onlineUsers);
      const isSelfMember =
        !!currentUserId && members.some(member => getMemberId(member) === currentUserId);

      if (isConnected && isSelfMember) {
        ids.add(currentUserId);
      }

      return ids;
    }, [onlineUsers, members, currentUserId, isConnected]);
    const onlineCount = members.reduce(
      (count, member) => (effectiveOnlineIds.has(getMemberId(member)) ? count + 1 : count),
      0,
    );
    const togglePin = useCallback(
      messageId => {
        if (!messageId) {
          return;
        }

        if (pinnedIds.includes(messageId)) {
          dispatch(unpinMessageAction({ chatId, messageId }));

          return;
        }

        dispatch(pinMessageAction({ chatId, messageId }));
      },
      [dispatch, chatId, pinnedIds],
    );
    const [replyToId, setReplyToId] = useState(null);
    const [activeEntityFilter, setActiveEntityFilter] = useState(null);

    const activeEntityFilterKey = activeEntityFilter
      ? getEntityReferenceKey(activeEntityFilter)
      : null;
    const entityFilteredMessages = useMemo(
      () =>
        activeEntityFilter
          ? messages.filter(message => messageReferencesEntity(message, activeEntityFilter))
          : messages,
      [messages, activeEntityFilter],
    );
    const onClearEntityFilter = useCallback(() => setActiveEntityFilter(null), []);

    // Reference-badge click (DEV-5446) — see useChatEntityFilter. Drives the SAME
    // `activeEntityFilter` state the rail's own item click sets via `setActiveEntityFilter`, so the
    // rail's own click behavior below is untouched.
    useChatEntityFilter({ chatId, onApply: setActiveEntityFilter });

    const replyParent = useMemo(() => {
      if (!replyToId) {
        return null;
      }

      return messages.find(message => getMessageId(message) === replyToId) ?? null;
    }, [messages, replyToId]);
    const onReplyTo = useCallback(messageId => {
      setReplyToId(messageId);
    }, []);
    const onCancelReply = useCallback(() => {
      setReplyToId(null);
    }, []);

    const currentChatUserId = useMemo(
      () => resolveCurrentChatUserId(members, currentUserId),
      [members, currentUserId],
    );

    const initialUnreadCountRef = useRef(null);
    const lastReadMessageIdRef = useRef(null);

    const [initialUnreadMessageId, setInitialUnreadMessageId] = useState(null);

    const lastOwnMessageAt = useMemo(() => {
      const lastOwn = [...messages].reverse().find(message => {
        const sender = getMessageSender(message, members);

        return !!sender.userId && sender.userId === currentUserId;
      });

      return lastOwn ? getMessageTimestamp(lastOwn) : null;
    }, [messages, members, currentUserId]);

    const onLoadMore = useCallback(() => {
      if (!cursor || !hasMore || isLoadingMessages) {
        return;
      }

      dispatch(fetchMessagesAction({ chatId, before: cursor }));
    }, [dispatch, chatId, cursor, hasMore, isLoadingMessages]);

    const onSendMessage = useCallback(
      ({ message, replyToMessageId = null }) => {
        dispatch(sendMessageAction({ chatId, currentUserId, message, replyToMessageId }));
        setReplyToId(null);
      },
      [dispatch, chatId, currentUserId],
    );

    const onUploadAttachment = useCallback(
      ({ file }) => {
        dispatch(uploadAttachmentAction({ chatId, file }));
      },
      [dispatch, chatId],
    );

    const onStartTyping = useCallback(() => {
      teamChatSignalRController.startTyping(chatId);
    }, [chatId]);

    const onStopTyping = useCallback(() => {
      teamChatSignalRController.stopTyping(chatId);
    }, [chatId]);

    useEffect(() => {
      if (!chatId || !messages.length) {
        return;
      }

      if (initialUnreadCountRef.current !== null) {
        return;
      }

      const unreadCount = channel?.unreadMessageCount ?? 0;

      initialUnreadCountRef.current = unreadCount;

      if (unreadCount <= 0 || unreadCount >= messages.length) {
        return;
      }

      const firstUnread = messages[messages.length - unreadCount];
      const firstUnreadId = firstUnread ? getMessageId(firstUnread) : null;

      if (firstUnreadId) {
        setInitialUnreadMessageId(firstUnreadId);
      }
    }, [chatId, channel, messages]);

    useEffectSafeStrictMode(() => {
      if (!chatId) {
        return;
      }

      dispatch(fetchMessagesAction({ chatId }));
      dispatch(fetchPinnedMessagesAction({ chatId }));
    }, [dispatch, chatId]);

    useEffect(() => {
      if (!chatId || !isActive) {
        return;
      }

      dispatch(setActiveChannelAction(chatId));

      return () => {
        dispatch(clearActiveChannelIfMatchesAction(chatId));
      };
    }, [dispatch, chatId, isActive]);

    useEffectSafeStrictMode(() => {
      if (!chatId || !isConnected) {
        return;
      }

      teamChatSignalRController.joinChannel(chatId);
      dispatch(fetchPresenceAction({ chatId }));

      return () => {
        teamChatSignalRController.leaveChannel(chatId);
      };
    }, [dispatch, chatId, isConnected]);

    useEffect(() => {
      if (!chatId || !isActive || !messages.length) {
        return;
      }

      const latestMessageId = getMessageId(messages[messages.length - 1]);

      if (!latestMessageId || latestMessageId === lastReadMessageIdRef.current) {
        return;
      }

      lastReadMessageIdRef.current = latestMessageId;
      dispatch(markAsReadAction({ chatId }));
    }, [dispatch, chatId, isActive, messages]);

    const title = getChannelDisplayName(channel) || fallbackTitle || '';

    return (
      <Box sx={rootStyles}>
        <TeamChatHeader
          endSlot={headerEndSlot}
          isHidden={isHeaderHidden}
          memberCount={members.length || null}
          onlineCount={onlineCount}
          title={title}
          totalCount={members.length || null}
        />
        <TeamChatPinnedStrip
          members={members}
          messages={messages}
          pinnedIds={pinnedIds}
          onUnpin={togglePin}
        />
        {showEntityReferencesRail && (
          <TeamChatReferencesRail
            activeEntityKey={activeEntityFilterKey}
            chatId={chatId}
            onClear={onClearEntityFilter}
            onSelectEntity={setActiveEntityFilter}
          />
        )}
        <TeamChatMessageList
          chatId={chatId}
          currentUserId={currentUserId}
          hasMore={hasMore}
          initialUnreadMessageId={initialUnreadMessageId}
          isLoading={isLoadingMessages}
          members={members}
          messages={entityFilteredMessages}
          pinnedIds={pinnedIds}
          onLoadMore={onLoadMore}
          onReplyTo={onReplyTo}
          onTogglePin={togglePin}
        />
        <TeamChatReadBy
          currentChatUserId={currentChatUserId}
          lastOwnMessageAt={lastOwnMessageAt}
          members={members}
          readReceipts={readReceipts}
        />
        <TeamChatTypingIndicator chatId={chatId} currentUserId={currentUserId} members={members} />
        {!!replyParent && (
          <TeamChatReplyPreview members={members} parent={replyParent} onCancel={onCancelReply} />
        )}
        <TeamChatMessageInput
          canShareCurrentView={canShareCurrentView}
          chatId={chatId}
          currentUserId={currentUserId}
          isDisabled={isSending || !chatId}
          members={members}
          priorityMemberIds={priorityMemberIds}
          replyToId={replyToId}
          onSendMessage={onSendMessage}
          onStartTyping={onStartTyping}
          onStopTyping={onStopTyping}
          onUploadAttachment={onUploadAttachment}
        />
      </Box>
    );
  },
);
TeamChatChannel.propTypes = {
  chatId: PropTypes.string,
  currentUserId: PropTypes.string,
  fallbackTitle: PropTypes.string,
  headerEndSlot: PropTypes.node,
  isHeaderHidden: PropTypes.bool,
  isActive: PropTypes.bool,
  canShareCurrentView: PropTypes.bool,
  priorityMemberIds: PropTypes.arrayOf(PropTypes.string),
  showEntityReferencesRail: PropTypes.bool,
};

export default TeamChatChannel;
